import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const DietaryAccommodations = () => {
  const accommodations = [
  {
    id: 1,
    icon: 'Leaf',
    title: 'Vegetarian Options',
    description: 'Extensive selection of vegetarian Italian dishes featuring fresh vegetables, cheeses, and house-made pasta',
    image: "https://images.unsplash.com/photo-1589552724612-f1e772af83ce",
    imageAlt: 'Colorful vegetarian Italian platter with grilled vegetables, fresh mozzarella, tomatoes, and basil on rustic wooden board',
    features: ['Eggplant Parmigiana', 'Caprese Salad', 'Vegetarian Lasagna', 'Margherita Pizza']
  },
  {
    id: 2,
    icon: 'Sprout',
    title: 'Vegan Selections',
    description: 'Plant-based Italian cuisine crafted with authentic flavors and premium ingredients',
    image: "https://images.unsplash.com/photo-1598504775793-2b3bc9c4bd3e",
    imageAlt: 'Vegan Italian pasta dish with fresh tomatoes, basil, and olive oil served in white ceramic bowl with herbs',
    features: ['Vegan Arancini', 'Pasta Primavera', 'Bruschetta', 'Vegan Tiramisu']
  },
  {
    id: 3,
    icon: 'Wheat',
    title: 'Gluten-Free Menu',
    description: 'Dedicated gluten-free options including pasta, bread, and desserts prepared in separate area',
    image: "https://images.unsplash.com/photo-1708989175833-9456d81ca179",
    imageAlt: 'Gluten-free Italian meal with rice pasta, grilled chicken, and fresh vegetables on elegant white plate',
    features: ['GF Pasta Dishes', 'GF Pizza Crust', 'GF Breadsticks', 'GF Desserts']
  },
  {
    id: 4,
    icon: 'Milk',
    title: 'Dairy-Free Options',
    description: 'Italian classics reimagined without dairy, maintaining authentic taste and texture',
    image: "https://images.unsplash.com/photo-1701520181989-660e20523497",
    imageAlt: 'Dairy-free Italian antipasto with cured meats, olives, roasted peppers, and artichokes on marble serving board',
    features: ['Dairy-Free Pesto', 'Olive Oil Based Sauces', 'Sorbet Desserts', 'Vegan Cheese Options']
  },
  {
    id: 5,
    icon: 'ShieldCheck',
    title: 'Nut-Free Preparation',
    description: 'Nut-free kitchen protocols ensuring safe dining for guests with nut allergies',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_154bd0a57-1764538650848.png",
    imageAlt: 'Nut-free Italian sandwich with fresh ingredients and Italian meats on artisan bread with safety certification label',
    features: ['Nut-Free Pesto', 'Safe Preparation Area', 'Ingredient Verification', 'Staff Training']
  },
  {
    id: 6,
    icon: 'Star',
    title: 'Halal Certified',
    description: 'Halal-certified meats and preparation methods following Islamic dietary guidelines',
    image: "https://images.unsplash.com/photo-1634307759364-a1d598b80257",
    imageAlt: 'Halal Italian meat platter with certified halal salami, prosciutto, and Italian cheeses with halal certification seal',
    features: ['Halal Meats', 'Certified Suppliers', 'Separate Preparation', 'Documentation Available']
  }];


  return (
    <section className="py-16 bg-card">
      <div className="container-custom">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 mb-4">
            <Icon name="Heart" size={18} color="var(--color-primary)" />
            <span className="text-sm font-medium text-primary">Inclusive Dining</span>
          </div>
          <h2 className="font-serif text-4xl font-bold text-foreground mb-4">
            Dietary Accommodations
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            We believe everyone deserves to enjoy authentic Italian cuisine. Our kitchen accommodates all dietary needs without compromising on flavor or quality.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {accommodations?.map((item) =>
          <div
            key={item?.id}
            className="bg-background rounded-2xl shadow-warm overflow-hidden hover:shadow-warm-lg transition-all duration-300 group">

              <div className="relative h-48 overflow-hidden">
                <Image
                src={item?.image}
                alt={item?.imageAlt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute top-4 left-4 w-12 h-12 rounded-full bg-background/90 backdrop-blur-sm flex items-center justify-center">
                  <Icon name={item?.icon} size={24} color="var(--color-primary)" />
                </div>
              </div>

              <div className="p-6">
                <h3 className="font-serif text-xl font-bold text-foreground mb-3">
                  {item?.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {item?.description}
                </p>

                <div className="space-y-2">
                  {item?.features?.map((feature, index) =>
                <div key={index} className="flex items-center gap-2">
                      <Icon name="Check" size={16} color="var(--color-success)" />
                      <span className="text-sm text-foreground">{feature}</span>
                    </div>
                )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-background rounded-2xl shadow-warm p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-serif text-2xl font-bold text-foreground mb-4">
                Custom Dietary Solutions
              </h3>
              <p className="text-muted-foreground mb-6">
                Have specific dietary requirements not listed here? Our culinary team specializes in creating custom solutions for any dietary need, including:
              </p>
              <ul className="space-y-3">
                {[
                'Low-sodium preparations',
                'Diabetic-friendly options',
                'Kosher catering available',
                'Allergen-free alternatives',
                'Religious dietary requirements',
                'Medical diet accommodations']?.
                map((item, index) =>
                <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="Check" size={14} color="var(--color-primary)" />
                    </div>
                    <span className="text-foreground">{item}</span>
                  </li>
                )}
              </ul>
            </div>

            <div className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="ShieldCheck" size={24} color="var(--color-accent)" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">
                    Safety First Approach
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    All dietary accommodations are prepared with strict protocols to prevent cross-contamination and ensure guest safety.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-background rounded-lg">
                  <Icon name="Users" size={20} color="var(--color-primary)" />
                  <span className="text-sm text-foreground">Trained staff on dietary needs</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-background rounded-lg">
                  <Icon name="FileText" size={20} color="var(--color-primary)" />
                  <span className="text-sm text-foreground">Detailed ingredient documentation</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-background rounded-lg">
                  <Icon name="AlertCircle" size={20} color="var(--color-primary)" />
                  <span className="text-sm text-foreground">Clear allergen labeling</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default DietaryAccommodations;